/**
 * Per-bay overrides for manually-drawn cabinet runs.
 *
 * An override targets one bay the user edited. It carries:
 *   - width? — make that bay a FIXED-WIDTH bay in the run sequence. The width
 *     is locked but the position is NOT: the bay holds its width and slides so
 *     the run always packs flush (no gaps, no overlaps). See the sequence-pack
 *     algorithm below.
 *   - type? — the bay's front ('standard' | 'drawer' | 'drawer-2' |
 *     'drawer-doors' | 'sink' | 'filler' | 'tall'). NON-locking: it only changes the front; the bay stays
 *     auto-sized and keeps reflowing.
 *
 * Override shape: { id, segmentIndex, anchorParam, width?, type? }.
 * A 'tall' override with a width is a SPAN — packed as modules of its own kind
 * to the upper-cabinet line, every box answering to the one override (proposal
 * §9 step 6); without a width it makes the auto bay containing its anchor tall.
 * A corner unit is not an auto bay, so its Unit-scope appearance is carried by a
 * separate corner override { id, cornerIndex, appearance } (no segmentIndex);
 * `cornerOverride` reads it and fillRun applies it to that corner cabinet.
 * `anchorParam` is the wall-param where the user clicked (the bay's centre at
 * override time) — used to place a fixed bay's preferred spot and to map an
 * appearance override onto whichever solved bay contains it. It is NOT a hard
 * position lock.
 *
 * Legacy runs stored `pins: [{ centerParam, width, type }]` (absolute pins).
 * `overridesForRun` migrates those to width+type overrides anchored at
 * centerParam, so old runs heal to the new model on the next solve.
 *
 * Pure. See claude-docs/2026-06-19-cabinet-run-override-model.md.
 */

const EPS = 1e-9;

/** Normalize a run's overrides, migrating legacy `pins` if present. */
function overridesForRun(run) {
  if (Array.isArray(run.bayOverrides)) return run.bayOverrides;
  if (Array.isArray(run.pins)) {
    return run.pins.map((p) => ({
      id: p.id,
      segmentIndex: p.segmentIndex,
      anchorParam: p.centerParam,
      width: p.width,
      type: p.type,
    }));
  }
  return [];
}

/**
 * The TIER an override addresses — which box in the column at that param.
 *
 * An upper and the lower beneath it are two boxes sharing one wall param, so an
 * override anchored by param alone cannot say which of them it means. Absent
 * `tier` is `'lower'`: every override written before tiers existed meant "this
 * lower cabinet", and structural overrides (width / type) are lower-only.
 */
const tierOf = (o) => o?.tier ?? 'lower';

/** A tall override: its bay — or, with a width, its whole span — rises to the upper-cabinet line. */
const isTallOverride = (o) => !!o && o.type === 'tall';

/** Overrides on a given segment, in a given tier. */
function overridesForSegment(run, segIndex, tier = 'lower') {
  return overridesForRun(run).filter(
    (o) => o.segmentIndex === segIndex && tierOf(o) === tier
  );
}

/**
 * Fixed-width overrides on a segment (those carrying a width), as
 * `{ anchorParam, width, override, splitIntoModules? }`. A tall SPAN is packed
 * as modules of its own kind — one override, many boxes — so it carries the
 * flag the packer reads; every other fixed width is one box. The packer never
 * reads `type`: this is the one place "tall" turns into a packing decision.
 */
const asSequencedOverride = (o) => ({
  anchorParam: o.anchorParam,
  width: o.width,
  override: o,
  ...(isTallOverride(o) ? { splitIntoModules: true } : {}),
});

function fixedWidthOverridesForSegment(run, segIndex) {
  return overridesForSegment(run, segIndex)
    .filter((o) => o.width > 0 && !isPositionedOverride(o))
    .map(asSequencedOverride);
}

/**
 * A POSITIONED override: one that holds its PLACE as well as its width.
 *
 * An ordinary fixed-width override is SEQUENCED — the packer pushes it at
 * whatever cursor the pieces before it reach, so its low edge is simply the
 * running total and cannot be moved. That is right for "make this cabinet
 * 24 inches" and wrong for "put this cabinet's edge HERE", which is what
 * dragging a bay's edge means: the user grabbed the low edge, and the only
 * thing that moved was the high one.
 *
 * A positioned override is handed to the packer as a HARD piece instead, so
 * it partitions the span and the free stretch BEFORE it is re-solved to the
 * smaller gap — the neighbours re-flow rather than being pinned, and a
 * stretch that cannot give comes back in `unsatisfied` rather than silently
 * sliding the bay. Nothing without the flag behaves any differently.
 */
const isPositionedOverride = (o) => !!o && o.positioned === true && o.width > 0;

/**
 * Positioned overrides on a segment, as HARD pieces — `{ center, width, bay }`,
 * the shape `planSubSpanSequence` takes for an appliance.
 *
 * The anchor IS the centre here, which is the whole point: a sequenced
 * override's `anchorParam` only picks which sub-span it belongs to, while a
 * hard piece's centre is where it actually lands.
 */
function positionedOverridesForSegment(run, segIndex) {
  return overridesForSegment(run, segIndex)
    .filter(isPositionedOverride)
    .map((o) => ({
      center: o.anchorParam,
      width: o.width,
      bay: {
        kind: o.kind || 'lower-cabinet',
        bayId: o.id,
        // What tells the consumer to DRAW a cabinet here. A hard piece is
        // otherwise an appliance or an opening, which emit nothing of their
        // own.
        cabinetOverride: o,
      },
    }));
}

/**
 * The overrides a ZONE can pack, split by what the zone can actually HOLD.
 *
 * A positioned override only means anything while the stretch it was placed in
 * still reaches around it. Three cases, and the middle one is the bug this
 * exists for:
 *
 *  - body inside the zone → a HARD piece, holding its place. The normal case.
 *  - body OVERHANGS the zone → the place cannot be honoured, so only the WIDTH
 *    survives and it packs as an ordinary sequenced override. It used to be
 *    handed over as a hard piece anyway, and `planSubSpanSequence` clamps a
 *    hard piece into the span — which is right for a measured appliance that
 *    really is standing there, and quite wrong for a cabinet whose position is
 *    nothing but a remembered gesture. The clamp slid it onto its neighbour
 *    and the neighbour was reported as overlapped and dropped: dragging a run's
 *    end OUT made cabinets disappear, which is the opposite of what lengthening
 *    a run should ever do.
 *  - centre outside the zone → nothing here can build it, so it is left out —
 *    and returned in `stranded`, never dropped in silence. A segment is ONE
 *    zone, so no other zone will pick it up. Left out quietly, the bay's
 *    stretch re-packed with the run's own widths and its front came back as
 *    plain doors: dragging the outer edge of a 12" drawer base past the run's
 *    end to 27" turned it into a door cabinet, the commit saw no fault and
 *    saved it, and the drawer record stayed in the document unused. The
 *    caller reports it as unsatisfied, which the edit gates refuse.
 *    `runEndEdit` still drops the ones a shortened wall segment strands, so
 *    shortening a run on purpose is not refused over them.
 *
 * The same holds for what STANDS in the zone (2026-10-17): a body that
 * overlaps one of the zone's hard pieces — an appliance moved onto it, its
 * side panels (`pad`) included — keeps its width and loses its place. Both
 * would otherwise be hard, and two hard pieces that overlap are both left
 * where they stand, so moving a fridge onto a placed cabinet drew the cabinet
 * through the fridge. Sequenced, it packs in the free stretch its anchor falls
 * in, beside the appliance, and takes its place back once nothing stands on
 * it. Nothing is written: the override is untouched. Its id is returned in
 * `displaced`, because where its width no longer fits in that stretch the
 * caller leaves it out (`fillSegmentZone`).
 *
 * @param {number} eps  the packer's own span tolerance, passed in so the two
 *                      agree about what "inside" means.
 * @param {Array<{center, width, pad?}>} standing  the zone's hard pieces
 * @returns {{ fixed, hard, displaced: Set<string>, stranded: Object[] }}
 *   `stranded` — the positioned override RECORDS this zone could not build.
 */
function overridesForZone(run, segIndex, lo, hi, eps = 0, standing = []) {
  const inZone = (p) => p >= lo - eps && p <= hi + eps;
  const standsOn = (start, end) =>
    standing.some((s) => {
      const reach = s.width / 2 + (s.pad || 0);
      return (
        Math.min(end, s.center + reach) - Math.max(start, s.center - reach) >
        eps
      );
    });
  const fixed = fixedWidthOverridesForSegment(run, segIndex).filter((f) =>
    inZone(f.anchorParam)
  );
  const hard = [];
  const displaced = new Set();
  const stranded = [];
  for (const h of positionedOverridesForSegment(run, segIndex)) {
    if (!inZone(h.center)) {
      stranded.push(h.bay.cabinetOverride);
      continue;
    }
    const half = h.width / 2;
    const start = h.center - half;
    const end = h.center + half;
    const held = inZone(start) && inZone(end);
    if (held && !standsOn(start, end)) {
      hard.push(h);
      continue;
    }
    if (held) displaced.add(h.bay.cabinetOverride.id);
    fixed.push(asSequencedOverride(h.bay.cabinetOverride));
  }
  return { fixed, hard, displaced, stranded };
}

/**
 * The override that governs a bay spanning `range` (params): its own
 * width-override (`ownOverride`) if any, else any appearance override whose
 * anchor falls inside the bay. Null when nothing governs it.
 */
function governingOverride(
  run,
  segIndex,
  range,
  ownOverride,
  tier = 'lower',
  except = null
) {
  if (ownOverride) return ownOverride;
  const lo = Math.min(range.start, range.end);
  const hi = Math.max(range.start, range.end);
  for (const o of overridesForSegment(run, segIndex, tier)) {
    // Skip fixed-width overrides — those govern their own bay via ownOverride.
    // A type-only OR appearance-only override (no width) governs the auto bay
    // it's anchored in — this is how a Unit-scope appearance reaches a bay.
    if (o.width != null) continue;
    // …unless the fill already built it a box of its own (`except`: a front
    // held to its line's widths, `fitBaysToLine`) — its anchor can then fall
    // in the box beside that one, which must not wear the front twice.
    if (except && except.has(o.id)) continue;
    if (o.anchorParam >= lo - EPS && o.anchorParam <= hi + EPS) return o;
  }
  return null;
}

/**
 * The appearance override targeting a corner unit, by its segment-pair index.
 * Corner overrides carry { id, cornerIndex, appearance } and no segmentIndex, so
 * they never govern an auto bay (overridesForSegment filters by segmentIndex) —
 * this is the only place they're read. Returns null when none targets the corner.
 */
function cornerOverride(run, cornerIndex, tier = 'lower') {
  if (cornerIndex == null) return null;
  for (const o of overridesForRun(run)) {
    // A corner UPPER carries the same `cornerIndex` as the corner lower it sits
    // above (corners.js stamps both), so the tier is the only thing separating
    // them — without it a Unit-scope edit on one repaints the other.
    if (o.cornerIndex === cornerIndex && tierOf(o) === tier) return o;
  }
  return null;
}

/**
 * The appearance ('standard' | 'drawer' | 'drawer-2' | 'drawer-doors' | 'sink' |
 * 'filler' | 'tall') for a bay. A bay's own width-override wins; otherwise any appearance
 * override anchored inside it. Default 'standard'.
 */
function resolveBayType(run, segIndex, range, ownOverride) {
  const o = governingOverride(run, segIndex, range, ownOverride);
  return (o && o.type) || 'standard';
}

/**
 * Apply a resolved appearance to a built lower-cabinet. Deterministic: it
 * resets the front to exactly `type` so switching between types fully replaces
 * the previous one (clears stale drawer/sink/filler/tall flags + columns). The
 * renderer draws drawers / the sink apron / the false panel from the `columns`
 * config, not the category.
 *   - 'drawer'       ⇒ stacked drawer fronts (a vendor family's stack is the
 *                      part it sells, set by the fill — `vendorDrawerStacks`)
 *   - 'drawer-2'     ⇒ a drawer base of TWO drawers (Venus's DB-2)
 *   - 'drawer-doors' ⇒ one real top drawer (with a handle) + doors below
 *   - 'sink'         ⇒ a fixed false apron panel on top + doors below
 *   - 'filler'       ⇒ a blank false panel (no door / handle) — a gap filler
 *   - 'standard'     ⇒ plain doors (no columns)
 *   - 'tall'         ⇒ a tall unit: uniform full-height doors (no columns), the
 *                    height set by the fill
 *   - 'trash'        ⇒ a drawer over a trash pull-out front (`pullOuts.js`)
 *   - 'spice'        ⇒ one full-height spice pull-out (`pullOuts.js`)
 *   - 'microwave'    ⇒ an open microwave space over a drawer
 *                    (`microwaveCabinets.js`); the fill places the microwave
 *   - 'end-shelf'    ⇒ open shelves round the run's open end (`endShelves.js`);
 *                    the fill stamps which side is open (`endShelfSide`)
 *   - 'angled-end'   ⇒ the run's end bay cut at 45° (`angledEnds.js`); the
 *                    fill stamps which side is open (`angledEndSide`)
 */
function applyBayType(cabinet, type) {
  const category = { ...(cabinet.category || {}) };
  category['lower-cabinet'] = true;
  delete category.drawer;
  delete category.twoDrawer;
  delete category.drawerDoors;
  delete category.sinkBase;
  delete category.filler;
  delete category.tall;
  delete category.endShelf;
  delete cabinet.endShelfSide;
  delete category.angledEnd;
  delete cabinet.angledEndSide;
  delete category.trashPullOut;
  delete category.spicePullOut;
  delete category.microwaveBase;

  if (type === 'drawer') {
    cabinet.label = 'Drawer Base';
    category.drawer = true;
    cabinet.category = category;
    cabinet.columns = [{ type: 'drawers', width: cabinet.dimensions.x }];
  } else if (type === 'drawer-2') {
    cabinet.label = '2-Drawer Base';
    category.drawer = true;
    category.twoDrawer = true;
    cabinet.category = category;
    cabinet.columns = [
      { type: 'drawers', width: cabinet.dimensions.x, drawerCount: 2 },
    ];
  } else if (type === 'drawer-doors') {
    cabinet.label = 'Drawer + Doors Base';
    category.drawerDoors = true;
    cabinet.category = category;
    cabinet.columns = [{ type: 'drawer-doors', width: cabinet.dimensions.x }];
  } else if (type === 'sink') {
    cabinet.label = 'Sink Base';
    category.sinkBase = true;
    cabinet.category = category;
    cabinet.columns = [{ type: 'sink-front', width: cabinet.dimensions.x }];
  } else if (type === 'filler') {
    cabinet.label = 'Cabinet Filler';
    category.filler = true;
    cabinet.category = category;
    cabinet.columns = [{ type: 'false-panel', width: cabinet.dimensions.x }];
  } else if (type === 'trash') {
    // a drawer over the bins' pull-out front (`pullOuts.js`; Venus B18TR)
    cabinet.label = 'Trash Pull-out Base';
    category.trashPullOut = true;
    cabinet.category = category;
    cabinet.columns = [
      {
        type: 'drawers',
        width: cabinet.dimensions.x,
        drawerCount: 2,
        graduated: true,
      },
    ];
  } else if (type === 'spice') {
    // one full-height pull-out (`pullOuts.js`; Venus BF09-PO)
    cabinet.label = 'Spice Pull-out Base';
    category.spicePullOut = true;
    cabinet.category = category;
    cabinet.columns = [
      {
        type: 'drawers',
        width: cabinet.dimensions.x,
        drawerCount: 1,
        // what slides out is a rack with three shelves ("Spice Rack w/ 3
        // Shelf"), reached from its open sides
        rack: { shelves: 3 },
      },
    ];
  } else if (type === 'microwave') {
    // an open microwave space over a drawer (`microwaveCabinets.js`; Venus
    // BMC30) — the fill puts the microwave in it
    cabinet.label = 'Microwave Base';
    category.microwaveBase = true;
    cabinet.category = category;
    cabinet.columns = [{ type: 'microwave', width: cabinet.dimensions.x }];
  } else if (type === 'end-shelf') {
    cabinet.label = 'Base End Shelf';
    category.endShelf = true;
    cabinet.category = category;
    cabinet.columns = [{ type: 'end-shelf', width: cabinet.dimensions.x }];
  } else if (type === 'angled-end') {
    // its outer front corner cut at 45° (`angledEnds.js`; Venus BEC24)
    cabinet.label = 'Angled End Base';
    category.angledEnd = true;
    cabinet.category = category;
    cabinet.columns = [{ type: 'angled-end', width: cabinet.dimensions.x }];
  } else if (type === 'tall') {
    // A tall unit. No columns: a column-less box renders as uniform full-
    // height doors (cabinetFrontLayout.js), which is what a pantry front is.
    // The height is the fill's (`emitBay`) — this only names the front.
    cabinet.label = 'Tall Cabinet';
    category.tall = true;
    cabinet.category = category;
    delete cabinet.columns;
  } else {
    cabinet.category = category;
    // `delete`, not `= undefined`. Firestore rejects an undefined value
    // ANYWHERE in a document, and the per-room write is a single update(): one
    // such field discards the whole room's post-processing silently. Harmless
    // while only the browser emitted these; the scan-run materializer now emits
    // them from the pipeline, straight into a Firestore write.
    delete cabinet.columns;
  }
}

// The cabinet cosmetics the renderer reads off an object. An "appearance
// bundle" is a subset of these governed together by one scope (room/run/unit).
// `texture` is a { textureId } object resolved to an image at render time;
// folding it in here lets an applied texture survive a re-solve like paint.
//
// `paintFinish` (matte | satin | gloss | high-gloss) is the paint's SHEEN and
// belongs with the colour it describes — `cabinet-finish` writes the pair and
// the renderer reads the pair. It was missing here, so a re-solve rebuilt a
// painted run in the default satin however it had been finished: the same
// loss as #1943's texture, one field over and one shade quieter.
//
// `doorDesign` is the family's door DESIGN a cabinet was bought as (what it
// sells and names); `doorStyle` is the drawn style that design renders as. Two
// fields for two concepts — product and geometry — and the renderer reads only
// `doorStyle` (`familyOffering`).
const APPEARANCE_FIELDS = [
  'doorStyle',
  'doorDesign',
  'hardwareType',
  'handleFinish',
  'paintColor',
  'paintFinish',
  'texture',
];

/**
 * Effective appearance bundle for a bay: the narrowest scope that has one wins
 * (unit override ?? run ?? room). Returns null when no scope sets appearance.
 * `ctx.roomAppearance` is the room default.
 */
function effectiveAppearance({ override, run, ctx }) {
  return (
    (override && override.appearance) ||
    (run && run.appearance) ||
    (ctx && ctx.roomAppearance) ||
    null
  );
}

/** Write an appearance bundle's cosmetic fields onto a built cabinet. */
function applyAppearanceBundle(cabinet, bundle) {
  if (!bundle) return;
  for (const f of APPEARANCE_FIELDS) {
    if (bundle[f] !== undefined) cabinet[f] = bundle[f];
  }
}

module.exports = {
  tierOf,
  isTallOverride,
  overridesForRun,
  overridesForSegment,
  fixedWidthOverridesForSegment,
  positionedOverridesForSegment,
  overridesForZone,
  isPositionedOverride,
  governingOverride,
  cornerOverride,
  resolveBayType,
  applyBayType,
  APPEARANCE_FIELDS,
  effectiveAppearance,
  applyAppearanceBundle,
};
